import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, MenuItem, Select, FormControl, InputLabel,
  Tabs, Tab, Checkbox, FormControlLabel, List, ListItem, ListItemText, Divider,
  CircularProgress,
} from '@mui/material';
import { Refresh, FolderOpen } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { insuranceCaseService, insurerService, tpaService } from '../../services/api';
import TopBar from '../../components/TopBar';

const STATUS_OPTIONS = [
  'PreAuthPending', 'PreAuthApproved', 'DocumentationPending', 'SubmittedToTPA',
  'QueryRaised', 'Approved', 'Settled', 'Rejected', 'Cancelled',
];

const STATUS_LABELS = {
  PreAuthPending: 'Pre-Auth Pending',
  PreAuthApproved: 'Pre-Auth Approved',
  DocumentationPending: 'Documentation Pending',
  SubmittedToTPA: 'Submitted to TPA',
  QueryRaised: 'Query Raised',
  Approved: 'Approved',
  Settled: 'Settled',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  PreAuthPending:       { bg: '#fff3e0', color: '#e65100' },
  PreAuthApproved:      { bg: '#e3f2fd', color: '#1565c0' },
  DocumentationPending: { bg: '#fff8e1', color: '#f57f17' },
  SubmittedToTPA:       { bg: '#e3f2fd', color: '#0277bd' },
  QueryRaised:          { bg: '#fce4ec', color: '#ad1457' },
  Approved:             { bg: '#e8f5e9', color: '#2e7d32' },
  Settled:              { bg: '#e8f5e9', color: '#1b5e20' },
  Rejected:             { bg: '#ffebee', color: '#c62828' },
  Cancelled:            { bg: '#eeeeee', color: '#616161' },
};

const StatusChip = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.PreAuthPending;
  return (
    <Box sx={{ px: 1.5, py: 0.3, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, bgcolor: c.bg, color: c.color, display: 'inline-block' }}>
      {STATUS_LABELS[status] || status}
    </Box>
  );
};

const TabPanel = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const InsuranceDeskPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [insurers, setInsurers] = useState([]);
  const [tpas, setTpas] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClaimType, setFilterClaimType] = useState('');
  const [filterInsurerId, setFilterInsurerId] = useState('');
  const [filterTpaId, setFilterTpaId] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Editable detail fields
  const [editForm, setEditForm] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: '', remarks: '' });
  const [checklist, setChecklist] = useState([]);

  useEffect(() => {
    Promise.all([insurerService.getAll(), tpaService.getAll()]).then(([ir, tr]) => {
      setInsurers(ir.data);
      setTpas(tr.data);
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterClaimType) params.claimType = filterClaimType;
      if (filterInsurerId) params.insurerId = filterInsurerId;
      if (filterTpaId) params.tpaId = filterTpaId;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const res = await insuranceCaseService.getAll(params);
      setCases(res.data);
    } catch {
      setError('Failed to load insurance cases');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterClaimType, filterInsurerId, filterTpaId, filterFrom, filterTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setTab(0);
    try {
      const res = await insuranceCaseService.getById(id);
      const c = res.data;
      setDetail(c);
      setEditForm({
        insurerId: c.insurerId || '',
        tpaId: c.tpaId || '',
        policyNumber: c.policyNumber || '',
        memberId: c.memberId || '',
        preAuthAmount: c.preAuthAmount ?? '',
        approvedAmount: c.approvedAmount ?? '',
        settledAmount: c.settledAmount ?? '',
        tpaReferenceNumber: c.tpaReferenceNumber || '',
        claimNumber: c.claimNumber || '',
        notes: c.notes || '',
      });
      setStatusForm({ status: c.status, remarks: '' });
      setChecklist(c.documents.map(d => ({ documentTypeId: d.documentTypeId, name: d.documentTypeName, description: d.documentTypeDescription, isCompleted: d.isCompleted })));
    } catch {
      setError('Failed to load case details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setEditForm(null);
  };

  const handleSaveSummary = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await insuranceCaseService.update(detail.id, {
        insurerId: editForm.insurerId || null,
        tpaId: editForm.tpaId || null,
        policyNumber: editForm.policyNumber,
        memberId: editForm.memberId,
        preAuthAmount: editForm.preAuthAmount === '' ? null : parseFloat(editForm.preAuthAmount),
        approvedAmount: editForm.approvedAmount === '' ? null : parseFloat(editForm.approvedAmount),
        settledAmount: editForm.settledAmount === '' ? null : parseFloat(editForm.settledAmount),
        tpaReferenceNumber: editForm.tpaReferenceNumber,
        claimNumber: editForm.claimNumber,
        notes: editForm.notes,
      });
      await openDetail(detail.id);
      loadData();
    } catch {
      setError('Failed to save case details');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await insuranceCaseService.updateStatus(detail.id, statusForm);
      await openDetail(detail.id);
      loadData();
    } catch {
      setError('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChecklist = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await insuranceCaseService.updateChecklist(detail.id, {
        entries: checklist.map(c => ({ documentTypeId: c.documentTypeId, isCompleted: c.isCompleted })),
      });
      await openDetail(detail.id);
      loadData();
    } catch {
      setError('Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'patientName', headerName: 'Patient', flex: 1, minWidth: 160 },
    { field: 'claimType', headerName: 'Claim Type', width: 130 },
    { field: 'insurerName', headerName: 'Insurer', flex: 1, minWidth: 150, renderCell: (p) => p.value || '—' },
    { field: 'tpaName', headerName: 'TPA', flex: 1, minWidth: 130, renderCell: (p) => p.value || '—' },
    {
      field: 'status', headerName: 'Status', width: 170,
      renderCell: (p) => <StatusChip status={p.value} />,
    },
    {
      field: 'admissionDate', headerName: 'Admission Date', width: 140,
      renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString() : '—',
    },
    { field: 'daysInStatus', headerName: 'Days in Status', width: 130 },
    {
      field: 'approvedAmount', headerName: 'Approved Amt', width: 130,
      renderCell: (p) => p.value != null ? `₹${p.value}` : '—',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Insurance Desk" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

          {/* Filter bar */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Claim Type</InputLabel>
                <Select value={filterClaimType} label="Claim Type" onChange={e => setFilterClaimType(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Cashless">Cashless</MenuItem>
                  <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Insurer</InputLabel>
                <Select value={filterInsurerId} label="Insurer" onChange={e => setFilterInsurerId(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {insurers.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>TPA</InputLabel>
                <Select value={filterTpaId} label="TPA" onChange={e => setFilterTpaId(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {tpas.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3} md={0.5}>
              <Button fullWidth variant="outlined" onClick={loadData} sx={{ height: '40px' }}>
                <Refresh fontSize="small" />
              </Button>
            </Grid>
          </Grid>

          <DataGrid
            rows={cases}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
            onRowClick={(params) => openDetail(params.row.id)}
            sx={{ cursor: 'pointer' }}
          />
        </Paper>
      </Container>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            Insurance Case {detail ? `#${detail.id}` : ''}
            {detail && (
              <Typography variant="body2" color="text.secondary">
                {detail.patientName} — {detail.claimType}
              </Typography>
            )}
          </Box>
          {detail && (
            <Button size="small" variant="outlined" startIcon={<FolderOpen />}
              onClick={() => navigate(`/insurance-cases/${detail.id}/packet`)}>
              Document Packet
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading || !detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}><StatusChip status={detail.status} /></Box>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Summary" />
                <Tab label="Status" />
                <Tab label="Documents" />
                <Tab label="History" />
              </Tabs>

              <TabPanel value={tab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Insurer</InputLabel>
                      <Select value={editForm.insurerId} label="Insurer"
                        onChange={e => setEditForm(p => ({ ...p, insurerId: e.target.value }))}>
                        <MenuItem value="">—</MenuItem>
                        {insurers.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>TPA</InputLabel>
                      <Select value={editForm.tpaId} label="TPA"
                        onChange={e => setEditForm(p => ({ ...p, tpaId: e.target.value }))}>
                        <MenuItem value="">—</MenuItem>
                        {tpas.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Policy Number" value={editForm.policyNumber}
                      onChange={e => setEditForm(p => ({ ...p, policyNumber: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Member ID" value={editForm.memberId}
                      onChange={e => setEditForm(p => ({ ...p, memberId: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="number" label="Pre-Auth Amount" value={editForm.preAuthAmount}
                      onChange={e => setEditForm(p => ({ ...p, preAuthAmount: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="number" label="Approved Amount" value={editForm.approvedAmount}
                      onChange={e => setEditForm(p => ({ ...p, approvedAmount: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="number" label="Settled Amount" value={editForm.settledAmount}
                      onChange={e => setEditForm(p => ({ ...p, settledAmount: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="TPA Reference Number" value={editForm.tpaReferenceNumber}
                      onChange={e => setEditForm(p => ({ ...p, tpaReferenceNumber: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Claim Number" value={editForm.claimNumber}
                      onChange={e => setEditForm(p => ({ ...p, claimNumber: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Notes" multiline rows={2} value={editForm.notes}
                      onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={handleSaveSummary} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={statusForm.status} label="Status"
                        onChange={e => setStatusForm(p => ({ ...p, status: e.target.value }))}>
                        {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Remarks" multiline rows={2} value={statusForm.remarks}
                      onChange={e => setStatusForm(p => ({ ...p, remarks: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" onClick={handleUpdateStatus} disabled={saving}>
                      {saving ? 'Updating…' : 'Update Status'}
                    </Button>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <List dense>
                  {checklist.map((doc, idx) => (
                    <ListItem key={doc.documentTypeId} divider>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={doc.isCompleted}
                            onChange={e => setChecklist(cur => cur.map((c, i) => i === idx ? { ...c, isCompleted: e.target.checked } : c))}
                          />
                        }
                        label={
                          <ListItemText primary={doc.name} secondary={doc.description} />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ mt: 1 }}>
                  <Button variant="contained" onClick={handleSaveChecklist} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Checklist'}
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={3}>
                <List dense>
                  {detail.statusHistory.map(h => (
                    <React.Fragment key={h.id}>
                      <ListItem>
                        <ListItemText
                          primary={`${STATUS_LABELS[h.status] || h.status}${h.changedByName ? ` — by ${h.changedByName}` : ''}`}
                          secondary={`${new Date(h.changedAt).toLocaleString()}${h.remarks ? ` — ${h.remarks}` : ''}`}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InsuranceDeskPage;
